import React, { useEffect, useRef } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames/bind';
import { get, unescape, isEmpty } from 'helpers/utilities';
import EventHandler from 'helpers/EventHandler';
import { slugify } from 'helpers/text';
import { EVENT } from 'config/constants';
import PersistentShare from 'components/PersistentShare';
import { jobLocationQuery } from 'queries/jobBenefits';
import useBreakpoint from '../../hooks/useBreakpoint';
import JobDetailCTA from '../JobDetailCTA';
import JobDetailPerksBenefits from '../JobDetailPerksBenefits';

import styles from './styles.scss';

const cx = classNames.bind(styles);

const JobDetailBody = props => {
  // Props
  const { data, jobBenefits, ctaImage, URLParams } = props;
  const jobDetailBodyRef = useRef();

  const breakpoint = useBreakpoint();
  const location = useLocation();

  // Refs
  const linkRef = useRef();
  const applyLinkRef = useRef();
  const shareButtonRef = useRef();
  const applicationForm = useRef();
  const offsetFrom = useRef(0);
  const offsetDist = useRef(0);
  const origTime = useRef(1000);
  const initialTime = useRef(0);
  const eventScrollRef = useRef();
  const grnhseIframe = useRef(null);

  const jobDetailBody = cx('jobDetailBody');
  const benefits = get(jobBenefits, '[0]jobBenefits[0]', {});
  const CTALocation = URLParams
    ? URLParams[1].replace(/[-/]/g, ' ')
    : get(data, 'offices[0].name', '');

  // componentDidMount
  useEffect(() => {
    /* Run isApplyNowVisible() when user scrolls on mobile breakpoint */
    if (breakpoint === 'mobile') {
      eventScrollRef.current = EventHandler.subscribe(EVENT.SCROLL, () =>
        isApplyNowVisible(linkRef, applyLinkRef, shareButtonRef)
      );
      applyLinkRef.current.addEventListener('click', scrollToForm);
    }

    if (data.title && data.id && window.Grnhse) {
      window.Grnhse.Config.IframeDefault.title = data.title;

      // Set the global iframe object
      grnhseIframe.current = window.Grnhse.Iframe.load(data.id);
    }

    return cleanup;
  }, [breakpoint]);

  useEffect(() => {
    const { current: linkRefCurrent } = linkRef;
    const { current: applyLinkRefCurrent } = applyLinkRef;

    if (linkRefCurrent) {
      linkRefCurrent.addEventListener('click', scrollToForm);
    }
    if (applyLinkRefCurrent && breakpoint === 'mobile') {
      applyLinkRefCurrent.addEventListener('click', scrollToForm);
    }

    return cleanup;
  }, [breakpoint]);

  useEffect(() => {
    /* Run isApplyNowVisible() when user scrolls on mobile breakpoint */
    if (breakpoint === 'mobile' && !eventScrollRef.current) {
      eventScrollRef.current = EventHandler.subscribe(EVENT.SCROLL, () =>
        isApplyNowVisible(linkRef, applyLinkRef, shareButtonRef)
      );
      applyLinkRef.current.addEventListener('click', scrollToForm);
    } else if (breakpoint !== 'mobile' && eventScrollRef.current) {
      EventHandler.unsubscribe(eventScrollRef.current);
    }

    if (get(data, 'id') && get(data, 'id') !== get(data, 'id') && window.Grnhse) {
      window.Grnhse.Config.IframeDefault.title = data.title;
      grnhseIframe.current = window.Grnhse.Iframe.load(data.id);
    }

    return cleanup;
  }, [breakpoint, data]);

  function scrollToForm() {
    offsetFrom.current = window.pageYOffset;
    offsetDist.current =
      applicationForm.current.offsetTop +
      applicationForm.current.offsetParent.offsetTop -
      offsetFrom.current;
    if (offsetDist.current < 0) {
      offsetDist.current -= 115;
    }
    initialTime.current = 0;
    window.requestAnimationFrame(time => loopScroll(time));
  }

  function cleanup() {
    const { current: linkRefCurrent } = linkRef;
    const { current: applyLinkRefCurrent } = applyLinkRef;

    if (eventScrollRef.current) {
      EventHandler.unsubscribe(eventScrollRef.current);
    }

    if (window.Grnhse && grnhseIframe.current) {
      // Unset the Html contents of the current instance
      // before creating new again.
      grnhseIframe.current.htmlElement = null;
    }

    if (linkRefCurrent) {
      linkRefCurrent.removeEventListener('click', scrollToForm);
    }
    if (applyLinkRefCurrent) {
      applyLinkRefCurrent.removeEventListener('click', scrollToForm);
    }
  }

  function isApplyNowVisible(button, button2, share) {
    const getScrollPosition = button.current.getBoundingClientRect();
    const scrollY = getScrollPosition.y;
    if (scrollY < -button.current.clientHeight) {
      button2.current.classList.add(styles.jobDetailBody__link__popup__active);
      share.current.setAttribute(
        'style',
        `transform: translateY(-${button.current.clientHeight}px); transition: 0.5s ease`
      );
    } else {
      button2.current.classList.remove(styles.jobDetailBody__link__popup__active);
      share.current.setAttribute('style', 'transform: translateY(0); transition: 0.5s ease');
    }
  }

  function loopScroll(time) {
    initialTime.current = initialTime.current || time;
    const currTime = time - initialTime.current;
    const dist = distancePerTime(currTime, origTime.current, offsetDist.current);
    if (currTime <= origTime.current) {
      window.scrollTo(0, dist + offsetFrom.current);
      window.requestAnimationFrame(loopScroll);
    }
  }

  function distancePerTime(currTime, origTime, offsetDist) {
    const per = currTime / origTime;
    return per * offsetDist;
  }

  function renderCTA() {
    const { data = {} } = useQuery(jobLocationQuery(slugify(CTALocation)));
    const { entry } = data;

    const children = (
      <>
        <JobDetailCTA
          className={styles.jobDetailBody__cta}
          locationCTA={CTALocation}
          ctaImage={ctaImage}
        />
        <button type="button" className={styles.jobDetailBody__link} ref={linkRef}>
          <span>Apply Now</span>
        </button>
      </>
    );

    return breakpoint === 'mobile'
      ? children
      : React.createElement(
          'div',
          {
            className: `${
              entry ? `${styles.jobDetailBody__left}` : `${styles.jobDetailBody__left__noCard}`
            }`
          },
          children
        );
  }

  return (
    <>
      <section className={`wrapper ${jobDetailBody}`} ref={jobDetailBodyRef}>
        {renderCTA()}
        <article className={styles.jobDetailBody__desc}>
          <div
            className={styles.jobDetailBody__content}
            dangerouslySetInnerHTML={{ __html: data ? unescape(data.content) : null }}
          />
        </article>
        {!isEmpty(benefits) && (
          <JobDetailPerksBenefits
            className={styles.jobDetailBody__benefits}
            jobBenefits={benefits}
          />
        )}
        {benefits.jobBenefitsDescription ? (
          <article className={styles.jobDetailBody__info}>
            <p dangerouslySetInnerHTML={{ __html: benefits.jobBenefitsDescription }} />
          </article>
        ) : null}
        <div ref={applicationForm} id="grnhse_app" className={styles.jobDetailBody__form} />
      </section>
      <PersistentShare
        title={CTALocation}
        url={location.pathname}
        socials={['facebook', 'twitter', 'linkedin', 'email']}
        shareRef={shareButtonRef}
      />
      <button
        type="button"
        className={`${styles.jobDetailBody__link} ${styles.jobDetailBody__link__popup}`}
        ref={applyLinkRef}>
        <span>Apply Now</span>
      </button>
    </>
  );
};

export default JobDetailBody;
